/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { resolveFormattingLocale } from '@/lib/formatting-locale';

type DatePartType = 'day' | 'month' | 'year';

export interface CalendarDay {
    date: Date;
    isoValue: string;
    label: string;
    isCurrentMonth: boolean;
    isSelected: boolean;
    isToday: boolean;
    isDisabled: boolean;
}

function createLocalDate(year: number, monthIndex: number, day: number): Date {
    return new Date(year, monthIndex, day, 12);
}

function isValidDateParts(year: number, month: number, day: number): boolean {
    if (!year || !month || !day) return false;

    const candidate = createLocalDate(year, month - 1, day);

    return candidate.getFullYear() === year && candidate.getMonth() === month - 1 && candidate.getDate() === day;
}

function getNumericDatePartOrder(locale?: string): DatePartType[] {
    const formatter = new Intl.DateTimeFormat(resolveFormattingLocale(locale), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });

    return formatter
        .formatToParts(createLocalDate(2006, 0, 2))
        .filter((part) => part.type === 'day' || part.type === 'month' || part.type === 'year')
        .map((part) => part.type as DatePartType);
}

export function parseIsoDateString(value?: string): Date | undefined {
    if (!value) return undefined;

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return undefined;

    const [, yearString, monthString, dayString] = match;
    const year = Number(yearString);
    const month = Number(monthString);
    const day = Number(dayString);

    if (!isValidDateParts(year, month, day)) {
        return undefined;
    }

    return createLocalDate(year, month - 1, day);
}

export function toIsoDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export function formatDateInputValue(value: string | undefined, locale?: string): string {
    const parsed = parseIsoDateString(value);
    if (!parsed) return '';

    return parsed.toLocaleDateString(resolveFormattingLocale(locale), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
}

export function parseTypedDateInput(input: string, locale?: string): Date | undefined {
    const trimmedValue = input.trim();
    if (!trimmedValue) return undefined;

    const isoDate = parseIsoDateString(trimmedValue);
    if (isoDate) return isoDate;

    const parts = trimmedValue.match(/\d+/g);
    if (!parts || parts.length !== 3) return undefined;

    const order = getNumericDatePartOrder(locale);
    const values = order.reduce<Partial<Record<DatePartType, number>>>((accumulator, part, index) => {
        accumulator[part] = Number(parts[index]);
        return accumulator;
    }, {});

    const year = values.year;
    const month = values.month;
    const day = values.day;

    if (!year || !month || !day || String(year).length < 4 || !isValidDateParts(year, month, day)) {
        return undefined;
    }

    return createLocalDate(year, month - 1, day);
}

export function isDateWithinBounds(date: Date, min?: string, max?: string): boolean {
    const comparisonValue = toIsoDateString(date);

    if (min && comparisonValue < min) return false;
    if (max && comparisonValue > max) return false;

    return true;
}

/**
 * Returns true when the date is in [min, max] AND, if an explicit allow-list is
 * provided, is also a member of that set. An empty/undefined `availableDateSet`
 * is treated as "no allow-list" so the date picker behaves identically to the
 * pre-OHDS-era usage.
 */
export function isDateAllowed(date: Date, min?: string, max?: string, availableDateSet?: Set<string>): boolean {
    if (!isDateWithinBounds(date, min, max)) return false;
    if (availableDateSet && availableDateSet.size > 0 && !availableDateSet.has(toIsoDateString(date))) {
        return false;
    }
    return true;
}

export function addMonths(date: Date, offset: number): Date {
    return createLocalDate(date.getFullYear(), date.getMonth() + offset, 1);
}

export function getMonthOptions(locale?: string): Array<{ value: number; label: string }> {
    const resolvedLocale = resolveFormattingLocale(locale);

    return Array.from({ length: 12 }, (_, monthIndex) => ({
        value: monthIndex,
        label: new Intl.DateTimeFormat(resolvedLocale, { month: 'short' }).format(createLocalDate(2025, monthIndex, 1)),
    }));
}

export function getYearOptions(displayDate: Date, min?: string, max?: string): number[] {
    const minYear = parseIsoDateString(min)?.getFullYear() ?? displayDate.getFullYear() - 100;
    const maxYear = parseIsoDateString(max)?.getFullYear() ?? displayDate.getFullYear() + 20;

    return Array.from({ length: maxYear - minYear + 1 }, (_, index) => minYear + index);
}

export function getWeekdayLabels(locale?: string): string[] {
    const resolvedLocale = resolveFormattingLocale(locale);
    const firstSunday = createLocalDate(2025, 0, 5);

    return Array.from({ length: 7 }, (_, index) => {
        const label = new Intl.DateTimeFormat(resolvedLocale, { weekday: 'short' }).format(
            createLocalDate(firstSunday.getFullYear(), firstSunday.getMonth(), firstSunday.getDate() + index)
        );

        return label.slice(0, 2);
    });
}

export function buildCalendarDays(
    displayDate: Date,
    selectedValue?: string,
    min?: string,
    max?: string,
    availableDateSet?: Set<string>
): CalendarDay[] {
    const selectedDate = parseIsoDateString(selectedValue);
    const today = createLocalDate(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
    const firstDayOfMonth = createLocalDate(displayDate.getFullYear(), displayDate.getMonth(), 1);
    const lastDayOfMonth = createLocalDate(displayDate.getFullYear(), displayDate.getMonth() + 1, 0);
    const startDate = createLocalDate(displayDate.getFullYear(), displayDate.getMonth(), 1 - firstDayOfMonth.getDay());
    const endDate = createLocalDate(
        displayDate.getFullYear(),
        displayDate.getMonth(),
        lastDayOfMonth.getDate() + (6 - lastDayOfMonth.getDay())
    );

    const days: CalendarDay[] = [];

    for (
        let cursor = startDate;
        cursor <= endDate;
        cursor = createLocalDate(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1)
    ) {
        const isoValue = toIsoDateString(cursor);

        days.push({
            date: cursor,
            isoValue,
            label: String(cursor.getDate()),
            isCurrentMonth: cursor.getMonth() === displayDate.getMonth(),
            isSelected: selectedDate ? isoValue === toIsoDateString(selectedDate) : false,
            isToday: isoValue === toIsoDateString(today),
            isDisabled: !isDateAllowed(cursor, min, max, availableDateSet),
        });
    }

    return days;
}
